import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useState } from 'react';
import { formatINR } from '@/lib/format';

// Dashboard charts for the Daily Management Summary. Colours are validated
// (scripts/validate_palette.js, light + dark): categorical order is fixed and never
// cycled; ageing uses one hue light → dark (older = darker = needs attention).

/** Categorical slots, in this order: passes CVD + contrast on light and dark surfaces. */
export const CAT = { green: '#059669', violet: '#8B5CF6', amber: '#D97706', blue: '#3B82F6' };
/**
 * Ageing buckets, newest → oldest: a distinct colour per bucket, cool (fresh) to deep
 * red (over 90 days). Validated: CVD-safe and ≥ 3:1 contrast on the light surface.
 */
export const AGE_COLORS = ['#0D9488', '#2563EB', '#D97706', '#DB2777', '#991B1B'];

export const rupees = (n: number) => `₹${formatINR(Math.round(n))}`;
/** ₹1.18 Cr / ₹25.1 L / ₹45.2K — axis + label form. */
export const short = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (a >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};

const TICK = { fontSize: 10, fill: 'var(--text-muted)' };
const GRID = { strokeDasharray: '3 3', stroke: 'var(--border-color)', vertical: false } as const;
const HIDDEN = 'xxxxx';

type TipRow = { name: string; value: number; color: string; extra?: string };
/** Shared hover card: title, then one line per series with its swatch. Text stays in ink colours. */
function TipCard({ title, rows, masked }: { title?: string; rows: TipRow[]; masked?: boolean }) {
  return (
    <div className="min-w-[150px] rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-3 py-2 text-[11px] shadow-lg">
      {title && <div className="mb-1 font-bold text-[var(--text-primary)]">{title}</div>}
      {rows.map((r) => (
        <div key={r.name} className="flex items-center justify-between gap-3 py-0.5">
          <span className="inline-flex items-center gap-1.5 text-[var(--text-secondary)]">
            <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: r.color }} />{r.name}
          </span>
          <span className="font-semibold tabular-nums text-[var(--text-primary)]">
            {masked ? HIDDEN : rupees(r.value)}{r.extra && !masked ? <span className="ml-1 font-normal text-[var(--text-muted)]">{r.extra}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Legend({ items }: { items: { label: string; color: string; value?: string }[] }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] text-[var(--text-secondary)]">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: it.color }} />
          {it.label}{it.value && <span className="font-semibold tabular-nums text-[var(--text-primary)]">{it.value}</span>}
        </span>
      ))}
    </div>
  );
}

/** Month-by-month lines on one ₹ axis, shaded underneath (crosshair + tooltip on hover). */
export function TrendLines({ data, series, masked, height = 230 }: {
  data: object[];
  series: { key: string; label: string; color: string }[];
  masked?: boolean;
  height?: number;
}) {
  return (
    <div>
      <Legend items={series.map((s) => ({ label: s.label, color: s.color }))} />
      <div className="mt-2" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            {/* Shade under each curve: the series colour fading to transparent at the baseline. */}
            <defs>
              {series.map((s) => (
                <linearGradient key={s.key} id={`trend-fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="month" tick={TICK} tickLine={false} axisLine={{ stroke: 'var(--border-color)' }} />
            <YAxis tick={TICK} tickLine={false} axisLine={false} width={masked ? 8 : 62} tickFormatter={(v: number) => (masked ? '' : short(v))} />
            <Tooltip
              cursor={{ stroke: 'var(--text-muted)', strokeWidth: 1, strokeDasharray: '3 3' }}
              content={({ active, payload, label }) => active && payload?.length
                ? <TipCard title={String(label)} masked={masked} rows={series.map((s) => ({ name: s.label, color: s.color, value: Number(payload.find((p) => p.dataKey === s.key)?.value ?? 0) }))} />
                : null}
            />
            {series.map((s) => (
              <Area key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} fill={`url(#trend-fill-${s.key})`} fillOpacity={1}
                dot={{ r: 4, strokeWidth: 2, stroke: 'var(--card-bg)', fill: s.color }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: 'var(--card-bg)' }} isAnimationActive={false} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/**
 * Bar list: one row per item — label and count on one line with the ₹ value at the
 * right, a slim bar underneath scaled to the largest row. Labels never wrap.
 */
export function BarList({ data, color, masked }: {
  data: { name: string; value: number; extra?: string }[];
  color: string;
  masked?: boolean;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="space-y-2.5">
      {data.map((d) => (
        <div key={d.name} title={masked ? d.name : `${d.name}: ${rupees(d.value)}${d.extra ? ` · ${d.extra}` : ''}`}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-[11.5px] font-semibold text-[var(--text-primary)]">
              {d.name}{d.extra && <span className="ml-1.5 font-normal text-[var(--text-muted)]">· {d.extra}</span>}
            </span>
            <span className="shrink-0 text-[13px] font-extrabold tabular-nums text-[var(--text-primary)]">{masked ? HIDDEN : short(d.value)}</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full" style={{ background: `${color}1F` }}>
            <div className="h-full rounded-full" style={{ width: `${Math.max(d.value > 0 ? 1.5 : 0, (d.value / max) * 100)}%`, background: color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Vertical columns (e.g. stock by age), value labelled on top. `height` may be "100%" to fill a sized parent. */
export function Columns({ data, colors, masked, height = 190 }: {
  data: { name: string; value: number; extra?: string }[];
  colors: string[];
  masked?: boolean;
  height?: number | string;
}) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 18, right: 4, left: 4, bottom: 0 }} barCategoryGap="22%">
          <CartesianGrid {...GRID} />
          <XAxis dataKey="name" tick={TICK} tickLine={false} axisLine={{ stroke: 'var(--border-color)' }} interval={0} />
          <YAxis hide domain={[0, (max: number) => max * 1.1]} />
          <Tooltip
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0].payload as { name: string; value: number; extra?: string };
              const i = data.findIndex((d) => d.name === p.name);
              return <TipCard masked={masked} rows={[{ name: p.name, value: p.value, color: colors[i], extra: p.extra }]} />;
            }}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={44} isAnimationActive={false}>
            {data.map((d, i) => <Cell key={d.name} fill={colors[i]} />)}
            {!masked && <LabelList dataKey="value" position="top" formatter={(v: unknown) => (Number(v) > 0 ? short(Number(v)) : '')} style={{ fontSize: 10, fontWeight: 700, fill: 'var(--text-primary)' }} />}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Donut (or pie when `pie`) with a 2px surface gap between slices. A donut shows the
 * hovered slice in its centre (never a floating box over the centre figure); a pie,
 * having no centre, uses a tooltip that may sit outside the chart so it is never clipped.
 * Hovering a slice or its legend row highlights both.
 */
export function Donut({ data, masked, centre, centreSub, pie, size = 170, valueText, stacked }: {
  /** `amount`: a ₹ figure shown in the legend beside the slice value (e.g. order value). */
  data: { name: string; value: number; color: string; extra?: string; amount?: number }[];
  masked?: boolean;
  centre?: string;
  centreSub?: string;
  pie?: boolean;
  size?: number;
  /** How a slice's value reads in the legend/tooltip (default ₹). */
  valueText?: (v: number) => string;
  /** Legend under the chart (narrow columns). */
  stacked?: boolean;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const total = data.reduce((a, d) => a + Math.max(0, d.value), 0);
  const fmt = valueText ?? rupees;
  const shown = data.filter((d) => d.value > 0);
  const pct = (v: number) => (total > 0 ? `${((Math.max(0, v) / total) * 100).toFixed(1)}%` : '0%');
  const hovered = hover != null ? data.find((d) => d.name === hover) : undefined;
  return (
    <div className={stacked ? 'flex flex-col items-center gap-3' : 'flex flex-col items-center gap-3 sm:flex-row sm:items-center'}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        {total > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={shown} dataKey="value" nameKey="name" innerRadius={pie ? 0 : '62%'} outerRadius="100%"
                startAngle={90} endAngle={-270} stroke="var(--card-bg)" strokeWidth={2} isAnimationActive={false}
                onMouseEnter={(_, i) => setHover(shown[i]?.name ?? null)} onMouseLeave={() => setHover(null)}>
                {shown.map((d) => <Cell key={d.name} fill={d.color} opacity={hover && hover !== d.name ? 0.35 : 1} style={{ cursor: 'pointer', transition: 'opacity 120ms' }} />)}
              </Pie>
              {pie && (
                <Tooltip
                  allowEscapeViewBox={{ x: true, y: true }}
                  wrapperStyle={{ zIndex: 30, pointerEvents: 'none' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0].payload as { name: string; value: number; color: string; extra?: string };
                    return (
                      <div className="whitespace-nowrap rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-3 py-2 text-[11px] shadow-lg">
                        <div className="flex items-center gap-1.5 font-bold text-[var(--text-primary)]"><span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />{p.name}</div>
                        <div className="mt-0.5 tabular-nums text-[var(--text-secondary)]">
                          {masked ? HIDDEN : fmt(p.value)} · {pct(p.value)}{p.extra && !masked ? ` · ${p.extra}` : ''}
                        </div>
                      </div>
                    );
                  }}
                />
              )}
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full w-full items-center justify-center rounded-full border-[14px] border-black/[0.06] text-[11px] text-[var(--text-muted)]">No data</div>
        )}
        {!pie && total > 0 && (centre || hovered) && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-[22%] text-center">
            {hovered ? (
              <>
                <div className="text-[13px] font-extrabold tabular-nums leading-tight text-[var(--text-primary)]">{masked ? HIDDEN : fmt(hovered.value)}</div>
                <div className="mt-0.5 truncate text-[9.5px] font-semibold uppercase tracking-wider text-[var(--text-muted)]" style={{ maxWidth: '100%' }}>{hovered.name}</div>
                <div className="text-[10px] font-bold tabular-nums" style={{ color: hovered.color }}>{pct(hovered.value)}</div>
              </>
            ) : (
              <>
                <div className="text-[15px] font-extrabold tabular-nums text-[var(--text-primary)]">{masked ? HIDDEN : centre}</div>
                {centreSub && <div className="text-[9.5px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">{centreSub}</div>}
              </>
            )}
          </div>
        )}
      </div>
      <div className="w-full min-w-0 space-y-0.5">
        {data.map((d) => (
          <div
            key={d.name}
            onMouseEnter={() => d.value > 0 && setHover(d.name)}
            onMouseLeave={() => setHover(null)}
            title={d.extra && !masked ? d.extra : undefined}
            className={`flex items-center justify-between gap-2 rounded px-1 py-0.5 text-[11px] transition-colors ${hover === d.name ? 'bg-accent/[0.08]' : ''}`}
          >
            <span className="inline-flex min-w-0 items-center gap-1.5 text-[var(--text-secondary)]">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: d.color }} />
              <span className="truncate">{d.name}</span>
            </span>
            <span className="shrink-0 tabular-nums">
              <span className="font-semibold text-[var(--text-primary)]">{masked ? HIDDEN : fmt(d.value)}</span>
              {d.amount != null && <span className="ml-2 inline-block min-w-[78px] text-right font-semibold text-[var(--text-secondary)]">{masked ? HIDDEN : rupees(d.amount)}</span>}
              <span className="ml-1.5 inline-block w-9 text-right text-[var(--text-muted)]">{total > 0 ? `${((Math.max(0, d.value) / total) * 100).toFixed(0)}%` : '0%'}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Waffle: 100 squares, one per 1% of the total, filled by category in order — a
 * part-to-whole read for a few parts (largest-remainder rounding keeps it at 100).
 * Hovering a category (square or legend row) dims the others.
 */
export function Waffle({ data, masked, valueText }: {
  data: { name: string; value: number; color: string; lines?: string[] }[];
  masked?: boolean;
  valueText?: (v: number) => string;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const fmt = valueText ?? rupees;
  const total = data.reduce((a, d) => a + Math.max(0, d.value), 0);
  // Largest-remainder: exact shares → whole squares that add up to 100.
  const raw = data.map((d) => (total > 0 ? (Math.max(0, d.value) / total) * 100 : 0));
  const cells = raw.map(Math.floor);
  const order = raw.map((r, i) => [r - Math.floor(r), i] as const).sort((a, b) => b[0] - a[0]);
  for (let k = 0; total > 0 && cells.reduce((a, b) => a + b, 0) < 100; k++) cells[order[k % order.length][1]] += 1;
  const squares = data.flatMap((d, i) => Array.from({ length: cells[i] }, () => d));
  if (total <= 0) return <div className="py-8 text-center text-[11px] text-[var(--text-muted)]">No stock</div>;
  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
      <div className="grid shrink-0 grid-cols-10 gap-[3px]" style={{ width: 190 }} role="img"
        aria-label={data.map((d, i) => `${d.name} ${cells[i]}%`).join(', ')}>
        {squares.map((d, i) => (
          <span key={i} onMouseEnter={() => setHover(d.name)} onMouseLeave={() => setHover(null)}
            className="aspect-square rounded-[3px] transition-opacity"
            style={{ background: d.color, opacity: hover && hover !== d.name ? 0.25 : 1 }} />
        ))}
      </div>
      <div className="w-full min-w-0 space-y-1.5">
        {data.map((d, i) => (
          <div key={d.name} onMouseEnter={() => setHover(d.name)} onMouseLeave={() => setHover(null)}
            className={`rounded-lg px-2 py-1.5 transition-colors ${hover === d.name ? 'bg-accent/[0.08]' : ''}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex min-w-0 items-center gap-1.5 text-[11.5px] font-bold text-[var(--text-primary)]">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: d.color }} />
                <span className="truncate">{d.name}</span>
              </span>
              <span className="shrink-0 text-[15px] font-extrabold tabular-nums" style={{ color: d.color }}>{cells[i]}%</span>
            </div>
            <div className="mt-0.5 pl-4 text-[11px] font-semibold tabular-nums text-[var(--text-primary)]">{masked ? HIDDEN : fmt(d.value)}</div>
            {d.lines?.map((l) => <div key={l} className="pl-4 text-[10.5px] text-[var(--text-secondary)]">{l}</div>)}
          </div>
        ))}
        <div className="px-2 text-[10px] text-[var(--text-muted)]">Each square = 1% of stock value</div>
      </div>
    </div>
  );
}
